import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  Menu,
  X,
  Search,
  ChevronDown,
  LogOut,
  User,
  Shield,
} from "lucide-react";
import { LANGUAGES, isRTL, getLocalizedPath, translatePathToLang } from "@/i18n";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useAuth } from "@/contexts/AuthContext";

export default function Header() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null);
  const menuRef = useRef(null);
  const { currency, setCurrency, currencies } = useCurrency();
  const { user, profile, logout, isInvestor, isAdmin, isStaff } = useAuth();
  const rtl = isRTL(i18n.language);

  const location = useLocation();
  const currentLangPath = `/${i18n.language}`;

  const navLinks = [
    { label: t("header.properties"), href: "#listings", type: "hash" },
    {
      label: t("header.investment_opportunities"),
      to: `${currentLangPath}/invest`,
      type: "link",
    },
    { label: t("header.why_kktc"), href: "#why-kktc", type: "hash" },
    { label: t("header.blog"), to: `${currentLangPath}/blog`, type: "link" },
    {
      label: t("header.about"),
      to: getLocalizedPath("about", i18n.language),
      type: "link",
    },
    {
      label: t("header.investor_relations"),
      to: `${currentLangPath}/investor-relations`,
      type: "link",
    },
    {
      label: t("header.contact"),
      to: `${currentLangPath}/contact`,
      type: "link",
    },
  ];

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await logout();
      setMenuOpen(null);
      navigate(`/${i18n.language}`);
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  const switchLanguage = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem("oly_lang", code);
    const newPath = translatePathToLang(location.pathname, code);
    navigate(newPath);
    setMenuOpen(null);
    setMobileOpen(false);
  };

  const scrollToSection = (href) => {
    setMobileOpen(false);
    if (location.pathname !== `/${i18n.language}`) {
      navigate(`/${i18n.language}`);
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 300);
    } else {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const currentLang =
    LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
        scrolled ? "bg-white border-b border-gray-200" : "bg-white/95 backdrop-blur-sm border-b border-gray-100"
      }`}
      dir={rtl ? "rtl" : "ltr"}
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to={`/${i18n.language}`}
            data-testid="header-logo"
            className="flex-shrink-0"
          >
            <span className="text-lg font-semibold text-gray-900 tracking-tight">
              OlyInvestment
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav
            className="hidden xl:flex items-center gap-8"
            data-testid="desktop-nav"
          >
            {navLinks.map((link) =>
              link.type === "link" ? (
                <Link
                  key={link.label}
                  to={link.to}
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  {link.label}
                </Link>
              ) : (
                <button
                  key={link.label}
                  onClick={() => scrollToSection(link.href)}
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  {link.label}
                </button>
              )
            )}
          </nav>

          {/* Right Section */}
          <div className="flex items-center gap-6" ref={menuRef}>
            {/* Language */}
            <div className="relative hidden lg:block">
              <button
                onClick={() => setMenuOpen(menuOpen === 'lang' ? null : 'lang')}
                className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                <span>{currentLang.code}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
              {menuOpen === 'lang' && (
                <div className={`absolute top-full right-0 mt-2 bg-white border border-gray-200 py-1 min-w-[120px] z-50`}>
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => switchLanguage(lang.code)}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                        i18n.language === lang.code ? "text-gray-900 font-medium" : "text-gray-600"
                      }`}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Currency */}
            <div className="relative hidden lg:block">
              <button
                onClick={() => setMenuOpen(menuOpen === 'currency' ? null : 'currency')}
                className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                <span>{currency.code}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
              {menuOpen === 'currency' && (
                <div className="absolute top-full right-0 mt-2 bg-white border border-gray-200 py-1 min-w-[100px] z-50">
                  {Object.entries(currencies).map(([code]) => (
                    <button
                      key={code}
                      onClick={() => {
                        setCurrency(code);
                        setMenuOpen(null);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                        currency.code === code ? "text-gray-900 font-medium" : "text-gray-600"
                      }`}
                    >
                      {code}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Auth */}
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setMenuOpen(menuOpen === 'account' ? null : 'account')}
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <User className="w-4 h-4" />
                  <span className="hidden xl:inline">{profile?.full_name?.split(' ')[0] || t('auth.my_account')}</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
                {menuOpen === 'account' && (
                  <div className="absolute top-full right-0 mt-2 bg-white border border-gray-200 py-1 min-w-[180px] z-50">
                    {(isAdmin || isStaff) && (
                      <Link
                        to="/staff"
                        onClick={() => setMenuOpen(null)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      >
                        <Shield className="w-4 h-4" />
                        Personel Paneli
                      </Link>
                    )}
                    <Link
                      to={
                        isAdmin || isStaff
                          ? `${currentLangPath}/admin`
                          : `${currentLangPath}/investor/dashboard`
                      }
                      onClick={() => setMenuOpen(null)}
                      className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                    >
                      {isInvestor ? t('header.investorPanel') : t('auth.my_account')}
                    </Link>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLogout();
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 flex items-center gap-2"
                    >
                      <LogOut className="w-4 h-4" />
                      {t("auth.logout")}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="hidden lg:flex items-center gap-4">
                <Link
                  to={`${currentLangPath}/giris`}
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  {t("auth.login")}
                </Link>
                <Link
                  to={`${currentLangPath}/kayit`}
                  className="text-sm text-gray-900 font-medium hover:text-gray-700 transition-colors"
                >
                  {t("auth.register")}
                </Link>
              </div>
            )}

            {/* Search */}
            <button className="hidden lg:block p-2 text-gray-600 hover:text-gray-900 transition-colors">
              <Search className="w-4 h-4" />
            </button>

            {/* Mobile Menu Toggle */}
            <button
              data-testid="mobile-menu-toggle"
              className="xl:hidden p-2 -mr-2"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? (
                <X className="w-5 h-5 text-gray-900" />
              ) : (
                <Menu className="w-5 h-5 text-gray-900" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="xl:hidden bg-white border-t border-gray-200">
          <nav className="px-6 py-4 space-y-1">
            {navLinks.map((link) =>
              link.type === "link" ? (
                <Link
                  key={link.label}
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  className="block py-3 text-sm text-gray-600 hover:text-gray-900 border-b border-gray-100 last:border-0"
                >
                  {link.label}
                </Link>
              ) : (
                <button
                  key={link.label}
                  onClick={() => scrollToSection(link.href)}
                  className="block w-full text-left py-3 text-sm text-gray-600 hover:text-gray-900 border-b border-gray-100 last:border-0"
                >
                  {link.label}
                </button>
              )
            )}
          </nav>
          <div className="px-6 py-4 border-t border-gray-200 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500 uppercase tracking-wider">{t("header.language")}</span>
              <div className="flex gap-2">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => switchLanguage(lang.code)}
                    className={`text-sm ${
                      i18n.language === lang.code ? "text-gray-900 font-medium" : "text-gray-600"
                    }`}
                  >
                    {lang.code}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500 uppercase tracking-wider">{t("header.currency")}</span>
              <div className="flex gap-2">
                {Object.keys(currencies).map((code) => (
                  <button
                    key={code}
                    onClick={() => setCurrency(code)}
                    className={`text-sm ${
                      currency.code === code ? "text-gray-900 font-medium" : "text-gray-600"
                    }`}
                  >
                    {code}
                  </button>
                ))}
              </div>
            </div>
            {user ? (
              <div className="space-y-2 pt-2">
                {(isAdmin || isStaff) && (
                  <Link
                    to="/staff"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-center gap-2 py-2.5 text-center text-sm text-[#0A192F] font-medium border border-gray-300 hover:bg-gray-50"
                  >
                    <Shield className="w-4 h-4" />
                    Personel Paneli
                  </Link>
                )}
                <Link
                  to={
                    isAdmin || isStaff
                      ? `${currentLangPath}/admin`
                      : `${currentLangPath}/investor/dashboard`
                  }
                  onClick={() => setMobileOpen(false)}
                  className="block py-2.5 text-center text-sm text-gray-600 border border-gray-300 hover:bg-gray-50"
                >
                  {isInvestor ? t('header.investorPanel') : t('auth.my_account')}
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    setMobileOpen(false);
                    handleLogout();
                  }}
                  className="w-full py-2.5 text-center text-sm text-red-600 border border-red-200 hover:bg-red-50 flex items-center justify-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  {t("auth.logout")}
                </button>
              </div>
            ) : (
              <div className="flex gap-4 pt-2">
                <Link
                  to={`${currentLangPath}/giris`}
                  onClick={() => setMobileOpen(false)}
                  className="flex-1 py-2.5 text-center text-sm text-gray-600 border border-gray-300 hover:bg-gray-50"
                >
                  {t("auth.login")}
                </Link>
                <Link
                  to={`${currentLangPath}/kayit`}
                  onClick={() => setMobileOpen(false)}
                  className="flex-1 py-2.5 text-center text-sm text-gray-900 bg-gray-100 hover:bg-gray-200"
                >
                  {t("auth.register")}
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
